import { useEffect, useRef } from 'react';
import { isNullish } from '@sas/nova-core/typeguards';
import { ColorObject } from '../types';
import { basicColors } from '../__internal__/BasicColorPallete';
import { isValidHex } from '../__internal__/utils/ColorPickerUtil';

/**
 * Performs validations on the color supplied by the consumer as well as if user selects nocolor option,
 * then provides previous selected color as lastSelectedColor.
 * @param selectedColor
 * @return an object consisting of selectedColor and lastSelectedColor.
 *
 */
export const useValidatedColorValues = (selectedColor: ColorObject) => {
  const defaultColor = '#000000';
  const { color, type } = selectedColor;

  const swatchColor =
    isNullish(color) || (!isValidHex(color!) && color.length !== 0)
      ? defaultColor
      : color.toUpperCase();

  const colorType = !type
    ? color === ''
      ? 'nocolor'
      : basicColors.includes(swatchColor)
      ? 'standard'
      : 'custom'
    : type;

  // save previously selected color in a ref if nocolor is selected
  const previousSelectedColor = useRef<ColorObject | null>(null);
  useEffect(() => {
    if (swatchColor !== '' || colorType !== 'nocolor') {
      previousSelectedColor.current = { color: swatchColor, type: colorType };
    }
  }, [swatchColor, colorType]);

  // if user selects nocolor, newSwatchColor should fallback to the previously selected color
  const lastSelectedColor: ColorObject =
    swatchColor !== '' && colorType !== 'nocolor'
      ? { color: swatchColor, type: colorType }
      : !previousSelectedColor.current
      ? { color: defaultColor, type: 'standard' }
      : previousSelectedColor.current;

  return {
    selectedColor: { color: swatchColor, type: colorType },
    lastSelectedColor,
  };
};
